/**
 * Small, typed preferences in `localStorage`.
 *
 * Deliberately not the autosave store: those are big documents in IndexedDB. These
 * are a handful of scalars that must be readable synchronously at boot, before the
 * first paint, so the editor does not flash the wrong theme.
 *
 * Everything degrades quietly — private mode, disabled storage and quota errors
 * all mean "no stored preference", which is the same as a first visit.
 */

const PREFIX = 'webpaint:prefs:v1:';

export interface Preferences {
  theme?: string;
  /** Whether the right panel is on screen (Iteration 13). '0' = closed. */
  rightPanelOpen?: string;
}

function storage(): Storage | null {
  try {
    const store = globalThis.localStorage;
    // Touch it: Safari in private mode throws on access, not on write.
    if (store) store.getItem(`${PREFIX}probe`);
    return store ?? null;
  } catch {
    return null;
  }
}

export function readPreference<K extends keyof Preferences>(key: K): Preferences[K] {
  try {
    const raw = storage()?.getItem(`${PREFIX}${String(key)}`);
    return raw === null || raw === undefined ? undefined : (raw as Preferences[K]);
  } catch {
    return undefined;
  }
}

export function writePreference<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
  try {
    const store = storage();
    if (!store) return;
    if (value === undefined || value === null) store.removeItem(`${PREFIX}${String(key)}`);
    else store.setItem(`${PREFIX}${String(key)}`, String(value));
  } catch {
    // A full or blocked store must never break the editor.
  }
}

/** Test seam: drop every stored preference. */
export function clearPreferences(): void {
  try {
    const store = storage();
    if (!store) return;
    // The key list is typed on purpose: adding a preference without adding it
    // here would leak state between tests that call clearPreferences().
    for (const key of Object.keys({ theme: '', rightPanelOpen: '' } as Preferences)) {
      store.removeItem(`${PREFIX}${key}`);
    }
  } catch {
    // ignore
  }
}
